import type { UseQueryResult } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"
import { usePublicPresentation } from "~/containers/PublicPresentation/public-presentation-context"

import type { ProposalSavingsDto } from "../dto/proposal.dto"
import { proposalService } from "../services/proposal.service"

/**
 * Retry for the savings read. A presentation's savings come with the payload,
 * so it never retries; while the gateway is still generating savings or the
 * quote, the read polls up to eight times; any other failure retries thrice.
 */
export const retryProposalSavings = (
  failureCount: number,
  error: unknown,
  inPresentation: boolean,
): boolean => {
  if (inPresentation) return false
  const message =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (error as any)?.response?.data?.message ?? (error as Error)?.message ?? ``
  const lowered = message.toLowerCase()
  const savingsDataIsStillGenerating =
    lowered.includes(`savings data is required`) ||
    // Preserve compatibility with the gateway's current validation copy.
    lowered.includes(`savings data is requried`)

  if (
    lowered.includes(`valid quote is required`) ||
    savingsDataIsStillGenerating
  ) {
    return failureCount < 8
  }
  return failureCount < 3
}

/** The savings poll backs off 750 ms per attempt, capped at five seconds. */
export const proposalSavingsRetryDelay = (attempt: number): number =>
  Math.min(5000, 750 * attempt)

export default function useFetchProposalSavings(
  proposalId: string,
  {
    enabled = true,
    queryScope,
  }: { enabled?: boolean; queryScope?: string } = {},
): UseQueryResult<ProposalSavingsDto, unknown> {
  const presentation = usePublicPresentation()
  const baseQueryKey = queryKeys.proposal.savings(proposalId)

  return useQuery({
    queryKey: presentation
      ? queryKeys.publicPresentation.savings(proposalId)
      : queryScope
        ? [...baseQueryKey, queryScope]
        : baseQueryKey,
    queryFn: () => {
      if (presentation) {
        if (!presentation.proposalSavings) {
          throw new Error(`Savings are not available for this presentation`)
        }
        return Promise.resolve(presentation.proposalSavings)
      }
      return proposalService.fetchProposalSavings(proposalId)
    },
    initialData: presentation?.proposalSavings ?? undefined,
    enabled: presentation
      ? true
      : enabled && !!proposalId && proposalId.length > 0,
    retry: (failureCount, error) =>
      retryProposalSavings(failureCount, error, Boolean(presentation)),
    retryDelay: proposalSavingsRetryDelay,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}
